import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { InputFormatOptions, formatInputValue, parseInputValue } from '../utils/inputFormatters';

interface FormattedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur'> {
  formatOptions: InputFormatOptions;
  value?: string | number;
  onChange?: (value: number | string) => void;
  onBlur?: (value: number | string) => void;
  label?: string;
  defaultText?: string;
  error?: string;
  helperText?: string;
}

const FormattedInput: React.FC<FormattedInputProps> = ({
  formatOptions,
  value = '',
  onChange,
  onBlur,
  label,
  defaultText,
  error,
  helperText,
  className = '',
  ...inputProps
}) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  // Update display value when value prop changes
  useEffect(() => {
    if (!isFocused) {
      const formattedValue = formatInputValue(String(value), formatOptions);
      setDisplayValue(formattedValue);
    }
  }, [value, formatOptions, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: inputValue } = e.target;

    // Keep the raw text while typing so decimals like "4.86" survive; formatting happens on blur
    setDisplayValue(inputValue);
    onChange?.(parseInputValue(inputValue, formatOptions));
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Show raw value when focused for easier editing
    const rawValue = parseInputValue(displayValue, formatOptions);
    setDisplayValue(String(rawValue));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    
    // Format the value on blur
    const formattedValue = formatInputValue(e.target.value, formatOptions);
    setDisplayValue(formattedValue);
    
    // Parse and pass the actual value to parent
    const parsedValue = parseInputValue(formattedValue, formatOptions);
    onBlur?.(parsedValue);
  };

  return (
    <>
      {label && (
        <label className="formatted-input-label">
          <b>{label}
          {formatOptions.type === 'currency' && <> ($)</>}
          {formatOptions.type === 'percent' && <> (%)</>}</b>
          {defaultText && <><i dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(defaultText, {
            ALLOWED_TAGS: ['br', 'b', 'i', 'strong', 'em', 'span'],
            ALLOWED_ATTR: []
          }) }} /></>}
        </label>
      )}
      <div>
      <input
        {...inputProps}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`formatted-input-field ${error ? 'error' : ''}`}
      />
      {error && (
        <div className="formatted-input-error">
          {error}
        </div>
      )}
      {helperText && !error && (
        <div className="formatted-input-helper">
          {helperText}
        </div>
      )}
    </div></>
  );
};

export default FormattedInput; 